import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type Props = {
  readonly current: number
  readonly totalScreens: number
  readonly isIntroScreen: boolean
  readonly isDoneScreen: boolean
  readonly canProceed: boolean
  readonly isFormValid: boolean
  readonly isSubmitting: boolean
  onPrevious: () => void
  onNext: () => void
  onFinish: () => void
}

export default function OnboardingNavigationFooter({
  current,
  totalScreens,
  isIntroScreen,
  isDoneScreen,
  canProceed,
  isFormValid,
  isSubmitting,
  onPrevious,
  onNext,
  onFinish,
}: Props) {
  const { t } = useTranslation()
  return (
    <div className='flex flex-shrink-0 items-center justify-between border-t bg-white px-1 pt-3 sm:px-0 sm:pt-4'>
      <Button
        variant='outline'
        onClick={onPrevious}
        disabled={isIntroScreen}
        className='min-w-[80px] text-xs sm:min-w-[100px] sm:text-base'
      >
        <ChevronLeft className='mr-0 h-3 w-3 sm:mr-1 sm:h-4 sm:w-4' />
        <span className='hidden sm:inline'>{t('needsAnalysis.navigation.previous')}</span>
        <span className='sm:hidden'>{t('needsAnalysis.navigation.previous')}</span>
      </Button>

      <div className='flex gap-1 sm:gap-1.5'>
        <span role='status' aria-live='polite' aria-atomic='true' className='sr-only'>
          {t('needsAnalysis.navigation.stepOf', { current: current + 1, total: totalScreens })}
        </span>
        {Array.from({ length: totalScreens }).map((_, index) => (
          <div
            key={index}
            aria-hidden='true'
            className={`h-1.5 rounded-full transition-all sm:h-2 ${index === current ? 'w-5 bg-blue-600 sm:w-8' : 'w-1.5 bg-gray-300 sm:w-2'}`}
          />
        ))}
      </div>

      {isDoneScreen ? (
        <Button
          onClick={onFinish}
          disabled={!isFormValid || isSubmitting}
          className='min-w-[80px] text-xs sm:min-w-[100px] sm:text-base'
        >
          {isSubmitting ? t('needsAnalysis.navigation.saving') : t('needsAnalysis.navigation.finish')}
        </Button>
      ) : (
        <Button
          className='btn-primary min-w-[80px] text-xs sm:min-w-[100px] sm:text-base'
          onClick={onNext}
          disabled={!canProceed}
        >
          <span className='hidden sm:inline'>{t('needsAnalysis.navigation.next')}</span>
          <span className='sm:hidden'>{t('needsAnalysis.navigation.next')}</span>
          <ChevronRight className='ml-1 h-3 w-3 sm:h-4 sm:w-4' />
        </Button>
      )}
    </div>
  )
}
